import React from 'react';
import PropTypes from 'prop-types';
import './OptimizedImage.scss';

/**
 * Optimize edilmiş görsel bileşeni
 * WebP desteği, lazy loading ve uygun boyutlandırma sağlar
 * 
 * @param {Object} props - Bileşen prop'ları
 * @param {string} props.src - Görsel kaynağı (jpg, png vb.)
 * @param {string} [props.webpSrc] - WebP formatındaki görsel (varsa)
 * @param {string} props.alt - Görsel alt metni
 * @param {number} [props.width] - Görselin genişliği
 * @param {number} [props.height] - Görselin yüksekliği
 * @param {string} [props.className] - Ek CSS sınıfları
 * @param {boolean} [props.lazy=true] - Lazy loading kullanımı
 * @param {boolean} [props.blurhash=false] - Blurhash görsel önizleme kullanımı
 * @param {string} [props.blurhashValue] - Blurhash değeri
 * @param {string} [props.mobileSrc] - Mobil için görsel kaynağı (jpg, png vb.)
 * @param {string} [props.tabletSrc] - Tablet için görsel kaynağı (jpg, png vb.)
 * @returns {JSX.Element}
 */
const OptimizedImage = ({
  src,
  webpSrc,
  alt,
  width,
  height,
  className = '',
  lazy = true,
  blurhash = false,
  blurhashValue,
  mobileSrc,
  tabletSrc,
  ...rest
}) => {
  // mobileSrc ve tabletSrc'yi sadece burada kullan, DOM'a aktarma
  const getFileExtension = (url) => {
    if (!url) return '';
    return url.split('.').pop().toLowerCase();
  };

  const imageType = getFileExtension(src);
  
  if (imageType === 'webp' || !webpSrc) {
    return (
      <img
        src={src}
        alt={alt}
        width={width}
        height={height}
        className={`optimized-image ${className}`}
        loading={lazy ? 'lazy' : undefined}
        {...rest}
      />
    );
  }

  return (
    <picture className={`optimized-picture ${className}`}>
      <source srcSet={webpSrc} type="image/webp" />
      <source srcSet={src} type={`image/${imageType}`} />
      <img
        src={src}
        alt={alt}
        width={width}
        height={height}
        loading={lazy ? 'lazy' : undefined}
        className="optimized-image"
        {...rest}
      />
    </picture>
  );
};

OptimizedImage.propTypes = {
  src: PropTypes.string.isRequired,
  webpSrc: PropTypes.string,
  alt: PropTypes.string.isRequired,
  width: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  height: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  className: PropTypes.string,
  lazy: PropTypes.bool,
  blurhash: PropTypes.bool,
  blurhashValue: PropTypes.string,
  mobileSrc: PropTypes.string,
  tabletSrc: PropTypes.string
};

export default OptimizedImage; 